'use client';

import cn from 'clsx';
import { Suspense, useEffect, useRef } from 'react';
import { BookmarkFilled, BookmarkUnfilled } from '../../tokens/icons';
import { convertPriceToNumber } from '../../utilities/convert-price-to-number';
import LinkManager, { LinkerComponent } from '../link-manager/LinkManager';
import s from './ProductCard.module.css';
import ProductVariants from './ProductVariants';
import {
    NostoProductCardAttributes,
    ProductCardProduct,
    ProductCardSliceVariant,
} from './types';

const HoverVideo = ({ src, poster }: { src: string; poster: string }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.play().catch(() => {});
        }
    }, []);

    return (
        <video
            ref={videoRef}
            muted
            playsInline
            loop
            width="640"
            height="900"
            className={s.video}
            poster={poster}
        >
            <source width="640" height="900" src={src} type="video/mp4" />
        </video>
    );
};

const VARIANT_STANDARD = 'standard';
const VARIANT_ALTERNATIVE = 'alternative';

export type PhotoVariant = 'flatlay' | 'model';

export type ProductCardProps = {
    metafields?: any;
    node?: any;
    variant?: ProductCardSliceVariant;
    product: ProductCardProduct;
    label?: string | null;
    coloursCount?: number;
    backgroundColour?: string;
    photoVariant?: PhotoVariant;
    nostoProductCardAttributes?: NostoProductCardAttributes;
    position?: number | null;
    currencyCode?: string;
    saved: boolean;
    toggleSaved?: (variantSKU: string, handle: string) => void;
    lang: string;
    Linker?: LinkerComponent;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
    hovered?: boolean;
};

export const ProductCard = ({
    variant = VARIANT_STANDARD,
    product,
    label,
    coloursCount,
    backgroundColour,
    photoVariant = 'flatlay',
    nostoProductCardAttributes,
    position = null,
    saved = false,
    toggleSaved,
    lang,
    Linker = LinkManager,
    onMouseEnter,
    onMouseLeave,
    onClick,
    hovered,
}: ProductCardProps) => {
    const {
        id,
        sku,
        handle,
        title,
        capsule,
        price,
        compareAtPrice,
        currencyCode,
        image,
        hoverImage,
        video,
        variants,
    } = product;

    const WishlistVector = saved ? BookmarkFilled : BookmarkUnfilled;
    const rootClassnames = cn(s.root, {
        [s.alternative]: variant === VARIANT_ALTERNATIVE,
    });
    const imageContainerProps = {
        style: {
            background: backgroundColour || 'auto',
        },
    };

    // Ensuring all the price types are numbers because they come from different sources such as Shopify and Nosto
    // and the upstream type is not guaranteed by the time we reach this code.
    let priceAsNumber =
        typeof price !== 'number' ? convertPriceToNumber(price) : price;
    let compareAtPriceAsNumber =
        typeof compareAtPrice !== 'number'
            ? convertPriceToNumber(compareAtPrice)
            : compareAtPrice;

    const showCompareAtPrice =
        compareAtPrice && compareAtPriceAsNumber > priceAsNumber;

    const handleOnClick = () => {
        if (onClick) {
            onClick();
        }

        if (
            id &&
            nostoProductCardAttributes?.isNostoProductCard === true &&
            typeof window !== 'undefined' &&
            typeof (window as any).nostojs === 'function'
        ) {
            (window as any).nostojs((api) => {
                api.recordAttribution({
                    type: 'vp',
                    target: id.split('/').slice(-1)[0],
                    ref: nostoProductCardAttributes.result_id,
                    refType: 'rec',
                }).done();
            });
        }
    };
    let activeImage = typeof image !== 'undefined' ? image : null;
    const showVideo = !!video?.url && (hovered || photoVariant === 'model');
    const videoPlaceholderPoster =
        photoVariant === 'model'
            ? ''
            : `${video?.poster}&width=1000&height=1333&crop=top`;

    if (!showVideo && (hovered || photoVariant === 'model')) {
        activeImage = hoverImage || activeImage;
    }

    const priceFormatter = new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
    });

    let coloursCountText = '1 Colour';
    if (coloursCount && coloursCount > 1) {
        coloursCountText = `${coloursCount} Colours`;
    }

    return (
        <Suspense fallback={<div>Loading product card...</div>}>
            <div
                className={rootClassnames}
                data-testid={`PC-SV-${handle}`}
                data-handle={handle}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={handleOnClick}
            >
                {!!toggleSaved && (
                    <button
                        className={s.wishlist}
                        onClick={(e) => {
                            e.preventDefault();
                            if (toggleSaved) toggleSaved(sku, handle);
                        }}
                    >
                        <WishlistVector />
                    </button>
                )}
                <Linker
                    passthrough={!handle}
                    href={`/products/${handle}`}
                    title={!showVideo ? `Go to product ${title}` : ''}
                    lang={lang}
                >
                    <div className={cn(s.image)} {...imageContainerProps}>
                        {!!activeImage && (
                            <img
                                src={`${activeImage}&width=1000&height=1333&crop=top`}
                                alt={`Product Image for ${title}`}
                                width="640"
                                height="900"
                                loading="lazy"
                            />
                        )}
                        {showVideo && (
                            <HoverVideo
                                src={video.url}
                                poster={videoPlaceholderPoster}
                            />
                        )}
                        {!activeImage && (
                            <div
                                className={cn(
                                    s.imagePlaceholder,
                                    'mmds-component-one-detail'
                                )}
                            >
                                Image Missing
                            </div>
                        )}
                        {coloursCount && hovered ? (
                            <div className={s.coloursCount}>
                                {coloursCountText}
                            </div>
                        ) : null}
                        {label && <div className={s.label}>{label}</div>}
                    </div>
                    <div className={s.summary}>
                        {hovered && (
                            <>
                                {capsule && (
                                    <div className={s.title}>{capsule}</div>
                                )}
                                <ProductVariants variants={variants} />
                            </>
                        )}
                        {!hovered && <div className={s.title}>{title}</div>}
                        {!hovered && !!price && (
                            <div className={s.pricing}>
                                {showCompareAtPrice && (
                                    <div className={s.compareAtPrice}>
                                        {priceFormatter.format(
                                            compareAtPriceAsNumber
                                        )}{' '}
                                        {currencyCode}
                                    </div>
                                )}
                                <div className={s.price}>
                                    {priceFormatter.format(priceAsNumber)}{' '}
                                    {currencyCode}
                                </div>
                            </div>
                        )}
                    </div>
                </Linker>
            </div>
        </Suspense>
    );
};

export default ProductCard;
